import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { logWithState } from '../functions/console.function';
import { buildFullSchemaGQL } from '../functions/request.function';
import { useStoreCommon } from '../hooks/useStore.hook';
import { LanguageEnum, Languages } from '../types/lang.type';
import { GQLContextType } from './gql.store';
import { RootStoreMobX } from './root.store';

const langMapper = {
  en: 'en_EN',
  fr: 'fr_FR',
  it: 'it_IT',
  de: 'de_DE',
} as const satisfies Record<string, Languages>;

function getNavigatorLanguage(): Languages {
  const navLang = navigator.language;
  const foundLang = Object.keys(langMapper).find((l) =>
    (navLang || '').toLowerCase().startsWith(l.toLowerCase()),
  );
  return foundLang ? langMapper[foundLang] : 'en_EN';
}

export type DocumentType = {
  _id: Languages;
  lang: Languages;
  translations: Record<string, string>;
};

export type DictionaryType = {
  _id: string;
  documents: DocumentType[];
  key: string;
  languages: Languages[];
  createdAt: string;
};

export type I18NContextType = {
  lang: Languages;
  langsAvailable: Languages[];
  dictionary: DictionaryType;
  injectDocumentTemp: Array<Omit<DocumentType, '_id'>>;
  queryI18n: string;
};

export class I18NStoreMobX {
  rootStore: RootStoreMobX;
  @observable public lang: I18NContextType['lang'] = getNavigatorLanguage();
  @observable public langsAvailable: I18NContextType['langsAvailable'] = [
    'en_EN',
  ];
  @observable public dictionary: I18NContextType['dictionary'] | null = null;
  @observable
  public injectDocumentTemp: I18NContextType['injectDocumentTemp'] = [];
  @observable public load: boolean = false;

  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @computed get currentLangLabel(): string {
    const langLabels: Record<Languages, string> = {
      en_EN: 'English',
      fr_FR: 'Français',
      it_IT: 'Italiano',
      de_DE: 'Deutsch',
    };
    return langLabels[this.lang] || 'English';
  }

  @computed get availableLangsCount(): number {
    return this.langsAvailable.length;
  }

  @computed get hasMultipleLangs(): boolean {
    return this.langsAvailable.length > 1;
  }

  @computed get allTranslations(): Record<string, string> | null {
    if (!this.dictionary) return null;
    const currentDoc = this.dictionary.documents.find(
      (doc) => doc.lang === this.lang,
    );
    if (!currentDoc) return null;

    const tempTranslations =
      this.injectDocumentTemp.find((doc) => doc.lang === this.lang)
        ?.translations || {};

    return { ...currentDoc.translations, ...tempTranslations };
  }

  @action public async setDictionary({
    client,
    introspection,
    queryI18n,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
    queryI18n?: I18NContextType['queryI18n'] | null;
  }) {
    const queryName = queryI18n || 'i18n';
    try {
      const { data: dataI18n } = await client.query<{
        i18n: DictionaryType;
      }>({
        query: buildFullSchemaGQL({
          operationName: queryName,
          operationType: 'QUERY',
          introspection: introspection,
        }),
        fetchPolicy: 'network-only',
      });

      runInAction(() => {
        this.dictionary = dataI18n[queryName];
        this.langsAvailable = dataI18n[queryName].languages;
      });
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'i18n:", error);
    }
  }

  @action public async init({
    client,
    introspection,
    injectDocumentTemp,
    queryI18n,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
    injectDocumentTemp?: I18NContextType['injectDocumentTemp'] | null;
    queryI18n?: I18NContextType['queryI18n'] | null;
  }) {
    runInAction(() => {
      this.load = false;
    });

    await this.setDictionary({ client, introspection, queryI18n });

    runInAction(() => {
      this.injectDocumentTemp = injectDocumentTemp || [];
      this.load = true;
    });
  }

  @action public setLang(lang: Languages) {
    this.lang = lang;
  }

  @action public async updateLang({
    client,
    introspection,
    lang,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
    lang: Languages;
  }) {
    try {
      const { data } = await client.mutate<{
        success: boolean;
      }>({
        mutation: buildFullSchemaGQL({
          operationName: 'updateLang',
          operationType: 'MUTATION',
          introspection: introspection,
        }),
        fetchPolicy: 'network-only',
        variables: {
          lang,
        },
      });

      runInAction(() => {
        this.setLang(lang);
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la langue:', error);
    }
  }
}

export const ConfigI18N = observer(
  ({
    children,
    injectDocumentTemp,
    queryI18n,
  }: {
    children: React.ReactNode;
    injectDocumentTemp?: I18NContextType['injectDocumentTemp'] | null;
    queryI18n?: I18NContextType['queryI18n'] | null;
  }) => {
    const { GQLStore, I18NStore, SSOStore } = useStoreCommon();

    useEffect(() => {
      if (
        !I18NStore.load &&
        GQLStore.load &&
        GQLStore.client &&
        GQLStore.introspection
      ) {
        logWithState({ state: 'INFO', value: 'ConfigI18N init' });
        I18NStore.init({
          client: GQLStore.client!,
          introspection: GQLStore.introspection,
          injectDocumentTemp,
          queryI18n,
        });
      }

      if (I18NStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigI18N load' });
      }
    }, [
      I18NStore.load,
      GQLStore.load,
      GQLStore.client,
      GQLStore.introspection,
      injectDocumentTemp,
      queryI18n,
    ]);

    useEffect(() => {
      if (SSOStore.user) {
        I18NStore.setLang(
          SSOStore.user.lang in LanguageEnum ? SSOStore.user.lang : 'en_EN',
        );
      }
    }, [SSOStore.user]);

    return I18NStore.load ? children : null;
  },
);
